import { CommonModule } from '@angular/common';
import { Component, input, signal, inject, effect, ViewChildren, QueryList } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Season } from '@solverml/api';
import { DragDropFilesComponent } from 'src/app/fragments/drag-drop-files/drag-drop-files.component';
import { ProgressBarComponent } from "../../../../fragments/progress-bar/progress-bar.component";
import { RfebmPrimaryButtonComponent } from 'src/app/ui/atoms/rfebm-primary-button/rfebm-primary-button.component';
import { FilterModel } from 'src/app/features/handball/video-quering/model/filter-model';
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VideoUploadStore } from 'src/app/pages/upload-video/store/upload.store';

@Component({
  selector: 'app-video-upload',
  templateUrl: './video-upload.component.html',
  styleUrls: ['./video-upload.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DragDropFilesComponent,
    ProgressBarComponent,
    RfebmPrimaryButtonComponent,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule
  ],
})
export class VideoUploadComponent {

  private static readonly MIN_VIDEO_RESOLUTION = 720;

  readonly filterValues = input.required<FilterModel>();
  readonly seasons = input<Season[]>([]);

  readonly title = signal<string>('');
  readonly description = signal<string>('');
  readonly seasonId = signal<number>(1);
  readonly matchDate = signal<string>(this.getTodayDate());

  selectedFile: File | null = null;
  selectedRecord: File | null = null;
  errorMessage: string | null = null;
  selectedFileDuration: number = 0;
  selectedFileWidth: number | null = null;
  selectedFileHeight: number | null = null;
  selectedFileResolutionError: string | null = null;
  private fileSelectionToken = 0;

  @ViewChildren(DragDropFilesComponent)
  dragDropComponents!: QueryList<DragDropFilesComponent>;

  private store = inject(VideoUploadStore);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);

  isEditing = signal<boolean>(false);

  constructor() {
    effect(() => {
      const editingVideo = this.store.editingVideo();
      if (editingVideo) {
        this.isEditing.set(true);
        this.title.set(editingVideo.title);
        this.description.set(editingVideo.description);
        this.seasonId.set(editingVideo.seasonId ?? 1);
        this.matchDate.set(editingVideo.matchDate ?? this.getTodayDate());
        this.selectedFile = editingVideo.file;
        this.selectedRecord = editingVideo.recordPdf;
        this.selectedFileDuration = 0;
        this.selectedFileWidth = editingVideo.resolutionWidth ?? null;
        this.selectedFileHeight = editingVideo.resolutionHeight ?? null;
        this.selectedFileResolutionError = editingVideo.minResolutionOk === false
          ? editingVideo.errorMessage ?? null
          : null;

        // Populate drag drops visually if possible:
        if (this.dragDropComponents) {
          const ds = this.dragDropComponents.toArray();
          if (ds[0] && this.selectedFile) ds[0].files = [this.selectedFile];
          if (ds[1] && this.selectedRecord) ds[1].files = [this.selectedRecord];
        }

      } else {
        this.isEditing.set(false);
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
  }

  isFormValid(): boolean {
    return this.title().trim().length >= 3 && this.description().trim().length > 0;
  }

  async onFileSelected(files: File[]) {
    if (files && files.length > 0) {
      const file = files[0];
      const currentSelectionToken = ++this.fileSelectionToken;

      if (!['video/mp4', 'video/avi', 'video/quicktime', 'video/x-msvideo'].includes(file.type)) {
        this.errorMessage = 'Formato de vídeo no soportado. Sube MP4, AVI o MOV.';
        this.selectedFile = null;
        this.selectedFileDuration = 0;
        this.selectedFileWidth = null;
        this.selectedFileHeight = null;
        this.selectedFileResolutionError = null;
        return;
      }

      this.selectedFile = file;
      this.errorMessage = null;
      this.selectedFileResolutionError = null;

      try {
        const metadata = await this.readVideoMetadata(file);
        if (currentSelectionToken !== this.fileSelectionToken) {
          return;
        }

        this.selectedFileDuration = metadata.duration;
        this.selectedFileWidth = metadata.width;
        this.selectedFileHeight = metadata.height;

        const validation = this.validateResolution(metadata.width, metadata.height);
        this.selectedFileResolutionError = validation.message;
      } catch {
        if (currentSelectionToken !== this.fileSelectionToken) {
          return;
        }
        this.selectedFileDuration = 0;
        this.selectedFileWidth = null;
        this.selectedFileHeight = null;
        this.selectedFileResolutionError = 'No se pudo leer la resolución del vídeo. Revisa el archivo e inténtalo de nuevo.';
      }
    } else {
      this.selectedFile = null;
      this.selectedFileDuration = 0;
      this.selectedFileWidth = null;
      this.selectedFileHeight = null;
      this.selectedFileResolutionError = null;
    }
  }

  onRecordSelected(files: File[]): void {
    if (files && files.length > 0) {
      this.selectedRecord = files[0];
    } else {
      this.selectedRecord = null;
    }
  }

  async onAddVideoClick() {
    if (!this.isFormValid()) {
      this.errorMessage = 'Por favor, completa el título (min 3 chars) y la descripción.';
      return;
    }

    if (!this.selectedFile) {
      this.errorMessage = 'Por favor, selecciona un archivo de video para subir.';
      return;
    }

    const filters = this.filterValues();
    // Validate mandatory filters (category, competition, gender)
    if (!filters || !filters.category || !filters.competition || !filters.gender ||
      filters.category === '' || filters.competition === '' || filters.gender === '') {
      this.errorMessage = 'Por favor, asegúrate de seleccionar género, categoría y competición.';
      return;
    }

    if (!this.seasonId()) {
      this.errorMessage = 'Selecciona una temporada válida.';
      return;
    }

    if (!this.selectedFileWidth || !this.selectedFileHeight) {
      try {
        const metadata = await this.readVideoMetadata(this.selectedFile);
        this.selectedFileDuration = metadata.duration;
        this.selectedFileWidth = metadata.width;
        this.selectedFileHeight = metadata.height;
        this.selectedFileResolutionError = this.validateResolution(metadata.width, metadata.height).message;
      } catch {
        this.selectedFileResolutionError = 'No se pudo leer la resolución del vídeo. Revisa el archivo e inténtalo de nuevo.';
      }
    }

    if (!this.selectedFileWidth || !this.selectedFileHeight) {
      this.errorMessage = 'No se pudo validar la resolución del vídeo.';
      return;
    }

    const resolutionValidation = this.validateResolution(this.selectedFileWidth, this.selectedFileHeight);
    this.selectedFileResolutionError = resolutionValidation.message;
    this.errorMessage = null;

    const isResolutionValid = resolutionValidation.valid;
    const qualityErrorMessage = resolutionValidation.message
      ?? `Calidad insuficiente (${this.selectedFileWidth}x${this.selectedFileHeight}). Solo se permiten vídeos de 720p o superior.`;

    if (this.isEditing() && this.store.editingVideoId()) {
      this.store.updateVideo(this.store.editingVideoId()!, {
        title: this.title(),
        description: this.description(),
        file: this.selectedFile,
        recordPdf: this.selectedRecord,
        category: filters.category,
        competition: filters.competition,
        gender: filters.gender,
        seasonId: this.seasonId(),
        matchDate: this.matchDate(),
        resolutionWidth: this.selectedFileWidth,
        resolutionHeight: this.selectedFileHeight,
        minResolutionOk: isResolutionValid,
        uploadStatus: isResolutionValid ? 'pending' : 'error',
        errorMessage: isResolutionValid ? undefined : qualityErrorMessage,
        uploadProgress: 0
      });
      this.store.setEditingVideo(null); // Clear editing mode

      if (!isResolutionValid) {
        this.snackBar.open(
          `Vídeo en error por resolución (${this.selectedFileWidth}x${this.selectedFileHeight}). Mínimo 720p.`,
          'Cerrar',
          { duration: 5000, panelClass: ['rfeb-warning-snackbar'] }
        );
      }
    } else {
      this.store.addVideo({
        title: this.title(),
        description: this.description(),
        file: this.selectedFile,
        recordPdf: this.selectedRecord,
        category: filters.category,
        competition: filters.competition,
        gender: filters.gender,
        seasonId: this.seasonId(),
        matchDate: this.matchDate(),
        resolutionWidth: this.selectedFileWidth,
        resolutionHeight: this.selectedFileHeight,
        minResolutionOk: isResolutionValid
      }, isResolutionValid ? undefined : {
        initialStatus: 'error',
        errorMessage: qualityErrorMessage
      });

      if (!isResolutionValid) {
        this.snackBar.open(
          `El vídeo "${this.title().trim() || this.selectedFile.name}" se marcó con error: resolución inferior a 720p.`,
          'Cerrar',
          { duration: 5000, panelClass: ['rfeb-warning-snackbar'] }
        );
      }
    }

    // Reset Form
    this.title.set('');
    this.description.set('');
    this.seasonId.set(1);
    this.matchDate.set(this.getTodayDate());
    this.selectedFile = null;
    this.selectedRecord = null;
    this.selectedFileDuration = 0;
    this.selectedFileWidth = null;
    this.selectedFileHeight = null;
    this.selectedFileResolutionError = null;

    // Reset drag drop UI children
    if (this.dragDropComponents) {
      this.dragDropComponents.forEach(component => component.files = []);
    }
  }

  navigateToAnalysis(): void {
    this.router.navigate(['/analysis']);
  }

  private getTodayDate(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private validateResolution(width: number, height: number): { valid: boolean; message: string | null } {
    const minDimension = Math.min(width, height);
    if (minDimension < VideoUploadComponent.MIN_VIDEO_RESOLUTION) {
      return {
        valid: false,
        message: `Calidad insuficiente (${width}x${height}). Solo se permiten vídeos de 720p o superior.`
      };
    }

    return { valid: true, message: null };
  }

  private readVideoMetadata(file: File): Promise<{ duration: number; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const videoElement = document.createElement('video');
      videoElement.preload = 'metadata';
      const objectUrl = URL.createObjectURL(file);

      const cleanup = () => {
        URL.revokeObjectURL(objectUrl);
        videoElement.removeAttribute('src');
        videoElement.load();
      };

      videoElement.onloadedmetadata = () => {
        const duration = Number.isFinite(videoElement.duration) ? videoElement.duration : 0;
        const width = videoElement.videoWidth || 0;
        const height = videoElement.videoHeight || 0;
        cleanup();
        resolve({ duration, width, height });
      };

      videoElement.onerror = () => {
        cleanup();
        reject(new Error('No se pudo leer el metadata del vídeo.'));
      };

      videoElement.src = objectUrl;
    });
  }
}
